@if (loading()) {
    <div class="flex flex-col h-full pb-4 px-4 gap-6 animate-pulse">
        <div class="h-4 w-48 rounded bg-white/10 mt-4"></div>
        <div class="flex gap-3 items-center">
            <div class="h-6 w-6 rounded-full bg-white/10"></div>
            <div class="h-6 w-96 rounded bg-white/10"></div>
            <div class="h-5 w-20 rounded-full bg-white/10"></div>
        </div>
        <div class="flex gap-4 flex-1 min-h-0">
            <div class="hidden md:block w-[400px] h-full rounded-sm bg-shui-base-2"></div>
            <div class="flex-1 rounded-sm bg-shui-base-2 flex flex-col gap-4 p-6">
                <div class="flex gap-6 border-b border-white/10 pb-4">
                    @for (i of [1, 2, 3]; track i) {
                        <div class="h-4 w-28 rounded bg-white/10"></div>
                    }
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    @for (field of [1, 2, 3, 4, 5, 6]; track field) {
                        <div class="flex flex-col gap-2">
                            <div class="h-3 w-20 rounded bg-white/10"></div>
                            <div class="h-9 rounded bg-white/10"></div>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
} @else if (request()) {
    <div class="flex flex-col h-full overflow-hidden pb-4 box-border">
        <nav class="flex w-full flex-shrink-0">
            <sh-breadcrumbs class="mt-4 mx-4">
                <sh-breadcrumb-item [label]="'breadcrumbs.home' | translate" href="/"></sh-breadcrumb-item>
                <sh-breadcrumb-item [label]="'breadcrumbs.my-requests' | translate" href="/tasks"></sh-breadcrumb-item>
                <sh-breadcrumb-item [label]="'MDLA-' + request()?.displayId" href="#"></sh-breadcrumb-item>
            </sh-breadcrumbs>
        </nav>

        <header class="flex flex-wrap justify-between items-start gap-4 mt-4 mb-6 px-4 flex-shrink-0 max-w-[1840px]">
            <div class="flex flex-wrap items-center gap-4">
                <div class="flex gap-4 items-center">
                    <app-urgent-button
                        [urgent]="request()?.isUrgent ?? false"
                        [disabled]="request()?.status !== statusEnum.SUBMITTED"
                        (change)="onUrgentChange($event)"
                    ></app-urgent-button>
                    <h1 class="text-lg sm:text-2xl font-bold text-shui-text-strong font-sh-bree-headline">
                        MDLA-{{ request()?.displayId }}: {{ request()?.subject }}
                    </h1>
                </div>
                <app-requests-status-pill
                    [status]="$any(request()?.status)"
                    [isOnHold]="request()?.isOnHold ?? false"
                ></app-requests-status-pill>
            </div>
            @if (request() && isTaskOwner()) {
                <app-change-status
                    [status]="$any(request()?.status)"
                    [request]="$any(request())"
                    (transitioning)="onTransitioning($event)"
                ></app-change-status>
            }
        </header>

        <div class="flex-1 min-h-0 flex flex-col md:flex-row gap-4 relative px-4">
            @if (transitioning()) {
                <div class="absolute inset-0 z-50 flex items-center justify-center rounded-sm bg-shui-base-2/80">
                    <div class="flex flex-col items-center gap-3">
                        <sh-spinner size="l"></sh-spinner>
                        <span class="text-shui-text-subtle text-sm">{{
                            'request.requestDetail.updating-status' | translate
                        }}</span>
                    </div>
                </div>
            }
            <div
                class="hidden md:flex max-h-full overflow-y-auto pr-2 bg-shui-base-2 w-full md:w-[400px] h-full rounded-md flex-col justify-between"
            >
                <app-status-stepper [requestStatus]="request()?.status"></app-status-stepper>
                <div class="flex flex-col gap-0 p-6">
                    <span class="text-sm text-shui-text-subtle">{{
                        'request.requestDetail.assigned-to' | translate
                    }}</span>
                    <span class="text-base text-shui-text-strong">
                        {{ request()?.assignee | userName | async }}
                    </span>
                </div>
            </div>

            <div class="flex flex-col gap-2 w-full md:max-w-[1440px] h-full flex-1 rounded-sm relative overflow-hidden">
                <div class="flex-1 min-h-0 flex flex-col bg-shui-base-2">
                    <div class="flex-shrink-0 z-10">
                        <sh-tabs>
                            <sh-tab-item
                                [label]="'request.requestDetail.tabs.request-information' | translate"
                                [attr.active]="selectedTab() === tabsEnum.REQUEST_INFORMATION ? true : null"
                                size="l"
                                (click)="setTab(tabsEnum.REQUEST_INFORMATION)"
                            ></sh-tab-item>
                            <sh-tab-item
                                [label]="'request.requestDetail.tabs.referrals' | translate"
                                [attr.active]="selectedTab() === tabsEnum.REFERRALS ? true : null"
                                size="l"
                                (click)="setTab(tabsEnum.REFERRALS)"
                            ></sh-tab-item>
                            <sh-tab-item
                                [label]="'request.requestDetail.tabs.comments' | translate"
                                [attr.active]="selectedTab() === tabsEnum.COMMENTS ? true : null"
                                size="l"
                                (click)="setTab(tabsEnum.COMMENTS)"
                            ></sh-tab-item>
                            <sh-tab-item
                                [label]="'request.requestDetail.tabs.activity-stream' | translate"
                                [attr.active]="selectedTab() === tabsEnum.ACTIVITY_LOG ? true : null"
                                size="l"
                                (click)="setTab(tabsEnum.ACTIVITY_LOG)"
                            ></sh-tab-item>
                        </sh-tabs>
                    </div>
                    <div
                        class="flex-1 min-h-0 bg-shui-base-2 overflow-y-auto py-4 px-6 flex flex-col gap-8"
                        [hidden]="selectedTab() !== tabsEnum.REQUEST_INFORMATION"
                    >
                        <app-deal-information-detail
                            [isInstrumentOnly]="request()?.isInstrumentOnlyDeal ?? false"
                            [productLine]="request()?.productLine ?? null"
                            [soldTo]="request()?.soldTo ?? null"
                            [shipTo]="request()?.shipTo ?? null"
                            [soldToName]="request()?.soldToName ?? null"
                            [soldToAddress]="request()?.soldToAddress ?? null"
                            [shipToAddress]="request()?.shipToAddress ?? null"
                            [materials]="request()?.materialItems ?? []"
                        ></app-deal-information-detail>
                        <app-order-details-detail
                            [isDistributor]="request()?.isDistributor ?? false"
                            [distributorName]="request()?.distributorName ?? null"
                            [isCprContract]="request()?.isCprContract ?? false"
                            [monthsOfInventory]="request()?.monthsOfInventory ?? null"
                            [additionalEmails]="request()?.additionalEmails ?? []"
                        ></app-order-details-detail>
                        <app-contact-information-detail
                            [customerEmail]="request()?.customerEmail ?? null"
                            [tasEmail]="request()?.tasEmail ?? null"
                            [salesRepEmail]="request()?.salesRepEmail ?? null"
                            [pmInstallationEmail]="request()?.pmInstallationEmail ?? null"
                            [additionalContacts]="request()?.additionalContacts ?? []"
                        ></app-contact-information-detail>
                        <app-submission-info-detail
                            [subject]="request()?.subject ?? null"
                            [reason]="request()?.reason ?? null"
                            [shipmentRequiredByDate]="request()?.shipmentRequiredByDate ?? null"
                            [isUrgent]="request()?.isUrgent ?? false"
                        ></app-submission-info-detail>
                    </div>
                    <div class="flex-1 min-h-0 py-4 px-6" [hidden]="selectedTab() !== tabsEnum.REFERRALS">
                        <sh-referral-overview
                            [referrals]="referrals()"
                            (referralClicked)="onReferralClicked($event)"
                        ></sh-referral-overview>
                    </div>
                    <div class="flex-1 min-h-0" [hidden]="selectedTab() !== tabsEnum.COMMENTS">
                        @if (requestId) {
                            <app-comments
                                [requestId]="requestId"
                                [currentUser]="me()"
                                [showInternal]="isTaskOwner()"
                                [reloadTrigger]="commentReloadTrigger()"
                            ></app-comments>
                        }
                    </div>
                    <div
                        class="flex-1 min-h-0 py-4 px-6 flex flex-col gap-8"
                        [hidden]="selectedTab() !== tabsEnum.ACTIVITY_LOG"
                    >
                        <app-activity-stream
                            [items]="activityItems()"
                            [loading]="activitiesLoading()"
                        ></app-activity-stream>
                    </div>
                </div>
            </div>
        </div>
    </div>
}
